"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

interface ProductImageLightboxProps {
	isOpen: boolean;
	onClose: () => void;
	images: string[];
	currentIndex: number;
	productName: string;
}

const ProductImageLightbox = ({
	isOpen,
	onClose,
	images,
	currentIndex,
	productName,
}: ProductImageLightboxProps) => {
	const [index, setIndex] = useState(currentIndex);
	const [isZoomed, setIsZoomed] = useState(false);
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const imageRef = useRef<HTMLDivElement>(null);

	// Reset index when opened with a new currentIndex
	useEffect(() => {
		setIndex(currentIndex);
	}, [currentIndex, isOpen]);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!isOpen) return;

			if (e.key === "Escape") {
				onClose();
			} else if (e.key === "ArrowLeft") {
				setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
			} else if (e.key === "ArrowRight") {
				setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose, images.length]);

	// Handle mouse movement for zoom
	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!isZoomed || !imageRef.current) return;

		const rect = imageRef.current.getBoundingClientRect();
		const x = ((e.clientX - rect.left) / rect.width) * 100;
		const y = ((e.clientY - rect.top) / rect.height) * 100;
		setMousePosition({ x, y });
	};

	// Handle navigation functions
	const goToPrevious = () => {
		setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
		setIsZoomed(false);
	};

	const goToNext = () => {
		setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
		setIsZoomed(false);
	};

	if (!isOpen) return null;
	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
					onClick={onClose}
				>
					<div
						className="relative max-w-screen-xl w-full h-full flex flex-col justify-center items-center p-4"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Close button */}
						<motion.button
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.2 }}
							onClick={onClose}
							className="absolute top-6 right-6 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-all duration-200 backdrop-blur-sm shadow-lg"
							aria-label="Close lightbox"
							whileHover={{ scale: 1.1 }}
							whileTap={{ scale: 0.95 }}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={2}
								stroke="currentColor"
								className="w-6 h-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</motion.button>

						{/* Main image container */}
						<div className="relative w-full h-[calc(100%-140px)] flex justify-center items-center overflow-hidden">
							<motion.div
								ref={imageRef}
								key={index}
								initial={{ opacity: 0, scale: 0.95 }}
								animate={{ opacity: 1, scale: isZoomed ? 2 : 1 }}
								exit={{ opacity: 0, scale: 0.95 }}
								transition={{ duration: 0.3 }}
								className={`relative w-full h-full max-h-[85vh] cursor-zoom-in transition-all duration-300 ${
									isZoomed ? "cursor-zoom-out" : ""
								}`}
								onClick={() => setIsZoomed(!isZoomed)}
								onMouseMove={handleMouseMove}
								style={
									isZoomed
										? {
												transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
										  }
										: {}
								}
							>
								<Image
									src={images[index]}
									alt={`${productName} - Full size image ${index + 1}`}
									fill
									sizes="100vw"
									className="object-contain rounded-lg shadow-2xl"
									priority
									quality={100}
									placeholder="blur"
									blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSI7IGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5YTlhOWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg=="
								/>
							</motion.div>

							{/* Navigation buttons */}
							{images.length > 1 && (
								<>
									<motion.button
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										transition={{ delay: 0.2 }}
										onClick={(e) => {
											e.stopPropagation();
											goToPrevious();
										}}
										className="absolute left-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-4 transition-all duration-200 backdrop-blur-sm shadow-lg"
										aria-label="Previous image"
										whileHover={{ scale: 1.1 }}
										whileTap={{ scale: 0.95 }}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth={2}
											stroke="currentColor"
											className="w-6 h-6"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M15.75 19.5L8.25 12l7.5-7.5"
											/>
										</svg>
									</motion.button>
									<motion.button
										initial={{ opacity: 0, x: 20 }}
										animate={{ opacity: 1, x: 0 }}
										transition={{ delay: 0.2 }}
										onClick={(e) => {
											e.stopPropagation();
											goToNext();
										}}
										className="absolute right-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-4 transition-all duration-200 backdrop-blur-sm shadow-lg"
										aria-label="Next image"
										whileHover={{ scale: 1.1 }}
										whileTap={{ scale: 0.95 }}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth={2}
											stroke="currentColor"
											className="w-6 h-6"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8.25 4.5l7.5 7.5-7.5 7.5"
											/>
										</svg>
									</motion.button>
								</>
							)}
						</div>

						{/* Image counter */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.3 }}
							className="text-white text-center mt-4"
						>
							<span className="text-sm font-medium bg-white/10 px-4 py-1.5 rounded-full backdrop-blur-sm shadow-lg">
								{index + 1} / {images.length}
							</span>
						</motion.div>

						{/* Zoom indicator */}
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.4 }}
							className="absolute top-4 left-4 bg-white/10 text-white text-sm font-medium px-4 py-2 rounded-full backdrop-blur-sm shadow-lg"
						>
							{isZoomed ? "Click to zoom out" : "Click to zoom in"}
						</motion.div>

						{/* Thumbnails at the bottom */}
						{images.length > 1 && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.3 }}
								className="mt-6 overflow-x-auto w-full max-w-4xl"
							>
								<div className="flex justify-center space-x-3 py-2">
									{images.map((image, i) => (
										<motion.button
											key={i}
											onClick={(e) => {
												e.stopPropagation();
												setIndex(i);
											}}
											className={`relative h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 transition-all duration-200 ${
												index === i
													? "scale-105 shadow-lg"
													: "opacity-60 hover:opacity-100 hover:scale-105"
											}`}
											whileHover={{ scale: 1.05 }}
											whileTap={{ scale: 0.95 }}
										>
											<Image
												src={image}
												alt={`${productName} - Thumbnail ${i + 1}`}
												fill
												sizes="80px"
												className="object-cover"
												loading="lazy"
												placeholder="blur"
												blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSI7IGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5YTlhOWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg=="
											/>
										</motion.button>
									))}
								</div>
							</motion.div>
						)}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default ProductImageLightbox;
